import { LoaderFunction, LoaderFunctionArgs } from 'react-router-dom';
import { client } from '../../../../gql/client';
import { store } from '../../../../state/configureStore';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { GET_ACTION_BUDGET_ITEMS_HISTORICAL } from '../../../../gql/queries/budgets/actionBudgetItems';
import { GET_BUDGET_CATEGORIES } from '../../../../gql/queries/budget/budgetCategories';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../gql/queries/actions/externalCompanies';
import { AuthContextType } from '../../../../hooks/useAuth';

export const SearchAndCreateHistoricalBudgetLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  if (userData?.user) {
    const dispatch = store.dispatch;
    const cursorBudgetItems = getCursor(
      tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS
    )(store.getState());

    // FIX A4: Si concessionId no está seteada, pre-setear si solo hay 1 concesión.
    let effectiveConcessionId = cursorBudgetItems.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS, { ...cursorBudgetItems, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido.
    dispatch(loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS, false));

    const readBudgetItems = client.readQuery({
      query: GET_ACTION_BUDGET_ITEMS_HISTORICAL,
      variables: {
        input: {
          take: cursorBudgetItems.take,
          cursor: cursorBudgetItems.cursor,
          orderBy: cursorBudgetItems.orderBy,
          budgetItemCode: cursorBudgetItems.budgetItemCode,
          categoryCode: cursorBudgetItems.categoryCode,
          fromDate: cursorBudgetItems.fromDate,
          toDate: cursorBudgetItems.toDate,
          indexExpedition: cursorBudgetItems.indexExpedition,
          indexType: cursorBudgetItems.indexType,
          concessionId: effectiveConcessionId,
          pkInitKm: cursorBudgetItems?.pkInitKm,
          pkInitMeter: cursorBudgetItems?.pkInitMeter,
          pkEndKm: cursorBudgetItems?.pkEndKm,
          pkEndMeter: cursorBudgetItems?.pkEndMeter,
          externalCompanyId: cursorBudgetItems?.externalCompanyId,
        },
      },
    });

    const pageInfo = readBudgetItems?.actionBudgetItemsHistorical?.pageInfo;
    const totalCount = readBudgetItems?.actionBudgetItemsHistorical?.totalCount;
    const isCacheEmpty = JSON.stringify(readBudgetItems) === '{}';

    const actionBudgetItems =
      readBudgetItems &&
      !isCacheEmpty &&
      readBudgetItems?.actionBudgetItemsHistorical?.actionBudgetItems;

    const [
      {
        data: { budgetCategories },
      },
      {
        data: { concessions },
      },
      {
        data: { externalCompanies },
      },
    ] = await Promise.all([
      client.query({
        query: GET_BUDGET_CATEGORIES,
      }),
      client.query({
        query: GET_CONCESSIONS,
      }),
      client.query({
        query: FETCH_EXTERNAL_COMPANIES,
      }),
    ]);

    const filtersDataOptions = {
      budgetCategories,
      concessions,
      externalCompanies,
    };

    const combinedData = {
      actionBudgetItems,
      filtersDataOptions,
      totalCount,
      pageInfo,
    };

    if (!readBudgetItems || isCacheEmpty) {
      dispatch(
        loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS, true)
      );

      try {
      let data;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        const effectiveTake = firstPageTake(cursorBudgetItems.take, takeBase);
        data = await client.query({
          query: GET_ACTION_BUDGET_ITEMS_HISTORICAL,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              orderBy: 'desc',
              budgetItemCode: cursorBudgetItems.budgetItemCode,
              categoryCode: cursorBudgetItems.categoryCode,
              fromDate: cursorBudgetItems.fromDate,
              toDate: cursorBudgetItems.toDate,
              indexExpedition: cursorBudgetItems.indexExpedition,
              indexType: cursorBudgetItems.indexType,
              concessionId: effectiveConcessionId,
              pkInitKm: cursorBudgetItems?.pkInitKm,
              pkInitMeter: cursorBudgetItems?.pkInitMeter,
              pkEndKm: cursorBudgetItems?.pkEndKm,
              pkEndMeter: cursorBudgetItems?.pkEndMeter,
              externalCompanyId: cursorBudgetItems?.externalCompanyId,
            },
          },
        });

        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          orderBy: 'desc',
          budgetItemCode: cursorBudgetItems.budgetItemCode,
          categoryCode: cursorBudgetItems.categoryCode,
          fromDate: cursorBudgetItems.fromDate,
          toDate: cursorBudgetItems.toDate,
          indexExpedition: cursorBudgetItems.indexExpedition,
          indexType: cursorBudgetItems.indexType,
          concessionId: effectiveConcessionId,
          pkInitKm: cursorBudgetItems?.pkInitKm,
          pkInitMeter: cursorBudgetItems?.pkInitMeter,
          pkEndKm: cursorBudgetItems?.pkEndKm,
          pkEndMeter: cursorBudgetItems?.pkEndMeter,
          externalCompanyId: cursorBudgetItems?.externalCompanyId,
        };

        dispatch(
          setCursor(
            tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS,
            setCursorData
          )
        );
      } else {
        data = await client.query({
          query: GET_ACTION_BUDGET_ITEMS_HISTORICAL,
          variables: {
            input: {
              take: cursorBudgetItems.take,
              cursor: cursorBudgetItems.cursor,
              orderBy: cursorBudgetItems.orderBy,
              budgetItemCode: cursorBudgetItems.budgetItemCode,
              categoryCode: cursorBudgetItems.categoryCode,
              fromDate: cursorBudgetItems.fromDate,
              toDate: cursorBudgetItems.toDate,
              indexExpedition: cursorBudgetItems.indexExpedition,
              indexType: cursorBudgetItems.indexType,
              concessionId: effectiveConcessionId,
              pkInitKm: cursorBudgetItems?.pkInitKm,
              pkInitMeter: cursorBudgetItems?.pkInitMeter,
              pkEndKm: cursorBudgetItems?.pkEndKm,
              pkEndMeter: cursorBudgetItems?.pkEndMeter,
              externalCompanyId: cursorBudgetItems?.externalCompanyId,
            },
          },
        });
      }

      const loading = data.loading;
      const pageInfo = data.data.actionBudgetItemsHistorical.pageInfo;
      const totalCount = data.data.actionBudgetItemsHistorical.totalCount;
      const actionBudgetItems =
        data.data.actionBudgetItemsHistorical.actionBudgetItems;

      const [
        {
          data: { budgetCategories },
        },
        {
          data: { concessions },
        },
        {
          data: { externalCompanies },
        },
      ] = await Promise.all([
        client.query({
          query: GET_BUDGET_CATEGORIES,
        }),
        client.query({
          query: GET_CONCESSIONS,
        }),
        client.query({
          query: FETCH_EXTERNAL_COMPANIES,
        }),
      ]);

      const filtersDataOptions = {
        budgetCategories,
        concessions,
        externalCompanies,
      };

      const combinedData = {
        actionBudgetItems,
        filtersDataOptions,
        totalCount,
        pageInfo,
        loading,
      };

      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
        dispatch(
          loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_BUDGETS, false)
        );
      }
    }

    return combinedData;
  }

  return {};
};

  return { data: load() };
};
